import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Car, 
  Clock, 
  Shield, 
  Star, 
  Phone, 
  Mail, 
  MapPin, 
  Users, 
  Zap, 
  Award,
  ChevronRight,
  MessageCircle
} from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import BookingModal from './BookingModal';

const LandingPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const AnimatedSection = ({ children, className = "" }) => {
    const controls = useAnimation();
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1
    });

    useEffect(() => {
      if (inView) {
        controls.start("visible");
      }
    }, [controls, inView]);

    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={fadeInUp}
        className={className}
      >
        {children}
      </motion.div>
    );
  };

  const openBookingModal = () => {
    setIsBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
  };

  const generateWhatsAppLink = () => {
    const message = `Hi Agami Cabs! 🚗

I'm interested in booking a premium ride. Could you please help me with:

📍 Service Areas: Delhi, Gurgaon, Noida
🚗 Services: Airport Transfer or Hourly Rental
💬 Please share availability and pricing.

Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/919999999999?text=${encodedMessage}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white overflow-x-hidden">
      <Navigation onBookingClick={openBookingModal} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1518308229454-f6b8237bced1')`
          }}
        />
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/20 to-transparent" />
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="relative z-10 text-center max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="bg-gradient-to-r from-white via-emerald-200 to-emerald-400 bg-clip-text text-transparent">
              Premium EV Luxury
            </span>
            <br />
            <span className="text-white">
              Redefined
            </span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Experience Delhi NCR's most sophisticated electric cab service. Professional chauffeurs, 
            pristine vehicles, and punctual service that puts <span className="text-emerald-400 font-semibold">you first, always</span>.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={openBookingModal}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-4 rounded-full text-white font-bold text-lg shadow-2xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Book Your Ride</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={generateWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white/30 px-8 py-4 rounded-full text-white font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 backdrop-blur-sm flex items-center space-x-2"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp</span>
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Floating Elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 left-10 w-2 h-2 bg-emerald-400 rounded-full hidden lg:block"
        />
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/3 right-20 w-3 h-3 bg-emerald-300 rounded-full hidden lg:block"
        />
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
                Premium Services
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Tailored luxury transportation solutions for every journey
              </p>
            </div>
          </AnimatedSection>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Airport Transfers */}
            <motion.div 
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                    <Car className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">Airport Transfers</h3>
                    <p className="text-emerald-400 font-medium">IGI Airport • Hassle-Free</p>
                  </div>
                </div>
                <p className="text-white/80 text-lg leading-relaxed mb-6">
                  Punctual, comfortable rides to and from IGI Airport. Track flights, meet & greet service, 
                  and zero waiting time with our premium airport transfer service.
                </p>
                <div className="flex items-center space-x-4 text-sm text-white/70 mb-4">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>24/7 Available</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Shield className="w-4 h-4" />
                    <span>Flight Tracking</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openBookingModal}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-2 rounded-xl text-white font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300"
                >
                  Book Airport Transfer
                </motion.button>
              </div>
            </motion.div>

            {/* Hourly/Daily Rentals */}
            <motion.div 
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">Hourly & Daily Rentals</h3>
                    <p className="text-emerald-400 font-medium">Flexible • Premium</p>
                  </div>
                </div>
                <p className="text-white/80 text-lg leading-relaxed mb-6">
                  Flexible hourly and daily rental options for business meetings, city tours, 
                  or special occasions. Dedicated chauffeur and pristine vehicle at your service.
                </p>
                <div className="flex items-center space-x-4 text-sm text-white/70 mb-4">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>Professional Driver</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4" />
                    <span>Premium Comfort</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openBookingModal}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-2 rounded-xl text-white font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300"
                >
                  Book Rental
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
                Premium EV Fleet
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Experience luxury with our meticulously maintained electric vehicles
              </p>
            </div>
          </AnimatedSection>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid md:grid-cols-2 gap-12"
          >
            {/* Tata T Express */}
            <motion.div 
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden"
            >
              <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1625998259975-b243bf480751" 
                  alt="Tata T Express"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Tata T Express</h3>
                <p className="text-emerald-400 font-medium mb-4">Premium Electric • 7 Seater</p>
                <p className="text-white/80 leading-relaxed mb-6">
                  Spacious luxury with cutting-edge electric technology. Perfect for families and groups 
                  seeking comfort and eco-friendly travel.
                </p>
                <div className="flex items-center space-x-6 text-sm text-white/70">
                  <div className="flex items-center space-x-1">
                    <Zap className="w-4 h-4" />
                    <span>350km Range</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>7 Passengers</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* BYD eMax 7 */}
            <motion.div 
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden"
            >
              <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1569780713905-3aab7e29a7e5" 
                  alt="BYD eMax 7"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">BYD eMax 7</h3>
                <p className="text-emerald-400 font-medium mb-4">Luxury Electric • Executive</p>
                <p className="text-white/80 leading-relaxed mb-6">
                  Sophisticated design meets electric innovation. Ideal for executive travel and 
                  special occasions with premium comfort features.
                </p>
                <div className="flex items-center space-x-6 text-sm text-white/70">
                  <div className="flex items-center space-x-1">
                    <Zap className="w-4 h-4" />
                    <span>420km Range</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Award className="w-4 h-4" />
                    <span>Executive Class</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-slate-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
                Book Your Premium Ride
              </h2>
              <p className="text-xl text-white/80">
                Ready to experience luxury? Get in touch with us today
              </p>
            </div>
          </AnimatedSection>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl p-8"
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Email</p>
                      <p className="text-emerald-400">hello@agamicabs.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Service Areas</p>
                      <p className="text-emerald-400">Delhi • Gurgaon • Noida</p>
                    </div>
                  </div>

                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href={generateWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 mt-4"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp Us</span>
                  </motion.a>
                </div>
              </div>

              <form className="space-y-4">
                <div>
                  <input 
                    type="text" 
                    placeholder="Your Name"
                    className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    placeholder="Email Address"
                    className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                  />
                </div>
                <div>
                  <textarea 
                    placeholder="Tell us about your ride requirements..."
                    rows="4"
                    className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-emerald-500 transition-colors duration-300 resize-none"
                  ></textarea>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-emerald-500/25"
                >
                  Send Message
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={isBookingModalOpen} onClose={closeBookingModal} />
    </div>
  );
};

export default LandingPage;